import { createWithEqualityFn } from "zustand/traditional";
import { shallow } from "zustand/shallow";
import { immer } from "zustand/middleware/immer";
import cloneDeep from "lodash/cloneDeep";
import dayjs from "dayjs";
import devDebug from "../Helpers/debug";
import i18n from "../i18n";
// import { current } from "immer";

export const defaultSupplierListNotice = {
  id: 0,
};

// define the initial state
const initialState = {
  supplierListNotice: null,
  averageDiscountForCPV: 0,
  supplierListNoticeStatus: "loading",
  supplierListNoticeError: "",
  submitting: false,
  errors: {},
  inputRefresh: Math.random(),
  currentLang: i18n.language.slice(0, 2),
  checkboxCPVs: {},
  normalCPVs: {},
  attachments: [],
  attachmentsStatus: "loading",
  attachmentsError: "",
  editAttachmentModalVisible: false,
  attachmentMode: "add",
  attachmentToBeEdited: null,
  soa_categories: [],
  soa_category_labels: [],
  tenderingAuthorityForInputDisplayOnly: "",
  converting: false,
  conversionError: "",
};

export const useViewSupplierListNotice = createWithEqualityFn(
  immer((set, get) => ({
    ...initialState,
    // ------------------- ALBO CONVERSIONS ----------------------

    setConverting: (converting) =>
      set((state) => {
        state.converting = converting;
      }),
    setConversionError: (conversionError) =>
      set((state) => {
        state.conversionError = conversionError;
      }),

    // ------------------- TENDERING AUTHORITIES ----------------------

    setTenderingAuthorityForInputDisplayOnly: (ta) =>
      set((state) => {
        state.tenderingAuthorityForInputDisplayOnly = ta;
      }),
    setTenderingAuthority: (ta) =>
      set((state) => {
        state.supplierListNotice.tendering_authority = ta.value;
        state.supplierListNotice.tendering_authority_name = ta.label;
      }),

    // ------------------- SOA ----------------------
    addSOACategory: (payload) =>
      set((state) => {
        if (!state.supplierListNotice.soa_categories.includes(payload.code)) {
          state.supplierListNotice.soa_categories = [...state.supplierListNotice.soa_categories, payload.code];
        }
        let included = false;
        for (let cat of state.supplierListNotice.soa_category_labels) {
          if (cat.code === payload.code) {
            included = true;
          }
        }
        if (!included) {
          state.supplierListNotice.soa_category_labels = [...state.supplierListNotice.soa_category_labels, payload];
        }
      }),
    removeSOACategory: (payload) =>
      set((state) => {
        state.supplierListNotice.soa_categories = state.supplierListNotice.soa_categories.filter((soa) => soa !== payload.code);
        state.supplierListNotice.soa_category_labels = state.supplierListNotice.soa_category_labels.filter((soa) => soa.code !== payload.code);
      }),
    // ------------------- ATTACHMENTS --------------
    setAttachments: (attachments) =>
      set((state) => {
        state.attachments = attachments;
      }),
    setAttachmentsStatus: (attachmentsStatus) =>
      set((state) => {
        state.attachmentsStatus = attachmentsStatus;
      }),
    setAttachmentsError: (attachmentsError) =>
      set((state) => {
        state.attachmentsError = attachmentsError;
      }),
    setEditAttachmentModalVisible: (payload) =>
      set((state) => {
        state.editAttachmentModalVisible = payload;
      }),
    setAttachmentMode: (attachmentMode) =>
      set((state) => {
        state.attachmentMode = attachmentMode;
      }),
    changeAttachmentAttribute: (name, value) =>
      set((state) => {
        state.attachmentToBeEdited[name] = value;
      }),
    setAttachment: (attachment) =>
      set((state) => {
        state.tender.attachment[Math.random()] = attachment;
      }),
    setAttachmentToBeEdited: (attachmentToBeEdited) =>
      set((state) => {
        state.attachmentToBeEdited = attachmentToBeEdited;
      }),
    // ------------------- CPVs --------------
    setCPVs: (payload) =>
      set((state) => {
        let convertedPayload = {};
        if (Array.isArray(payload)) {
          for (let cpv of payload) {
            convertedPayload[cpv] = true;
          }
          payload = convertedPayload;
        }

        state.supplierListNotice.cpvs = Object.keys(payload).filter((key) => !payload?.[key]?.partialChecked);
        let withPartialCheckedObj = {};
        let withoutPartialCheckedObj = {};
        let hasPartialAttribute = false;
        if (Object.keys(payload).length > 0 && payload[Object.keys(payload)[0]].hasOwnProperty("partialChecked")) {
          hasPartialAttribute = true;
        }
        if (hasPartialAttribute) {
          state.checkboxCPVs = payload;
          for (let cpv of Object.keys(payload)) {
            if (!payload[cpv].partialChecked) {
              withoutPartialCheckedObj[cpv] = true;
            }
          }
          state.normalCPVs = withoutPartialCheckedObj;
        } else {
          for (let cpv of Object.keys(payload)) {
            withPartialCheckedObj[cpv] = { checked: true, partialChecked: false };
          }
          state.checkboxCPVs = withPartialCheckedObj;
          state.normalCPVs = payload;
        }
      }),
    deleteCPV: (payload) =>
      set((state) => {
        state.supplierListNotice.cpvs = state.supplierListNotice.cpvs.filter((cpv) => cpv !== payload);
        delete state.checkboxCPVs[payload];
        delete state.normalCPVs[payload];
      }),
    // ------------------------------ INPUTS ---------------------------------
    setErrors: (errors) =>
      set((state) => {
        state.errors = errors;
      }),
    setCurrentLang: (currentLang) =>
      set((state) => {
        state.currentLang = currentLang;
      }),
    setInputRefresh: () =>
      set((state) => {
        state.inputRefresh = Math.random();
      }),
    setSubmitting: (submitting) =>
      set((state) => {
        state.submitting = submitting;
      }),
    setSupplierListNotice: (supplierListNotice) =>
      set((state) => {
        state.supplierListNotice = supplierListNotice;
        state.tenderingAuthorityForInputDisplayOnly = supplierListNotice?.tendering_authority_name || ""; //needed for pre-populating TA search input

      }),
    setSupplierListNoticeValue: (e) =>
      set((state) => {
        let value;
        if (e.target.value === "true" || e.target.value === "false") {
          if (e.target.value === "true") {
            value = true;
          } else {
            value = false;
          }
        } else {
          value = e.target.value;
        }
        if (e.target.lang) {
          if (e.target.lang === "og") {
            e.target.lang = state.supplierListNotice.original_language;
          }
          if (state.supplierListNotice.translations[e.target.lang]) {
            state.supplierListNotice.translations[e.target.lang][e.target.name] = value;
          } else {
            state.supplierListNotice.translations[e.target.lang] = {
              title: "",
            };
            state.supplierListNotice.translations[e.target.lang][e.target.name] = value;
          }
        } else {
          state.supplierListNotice[e.target.name] = value;
        }
      }),
    setSupplierListNoticeError: (supplierListNoticeError) =>
      set((state) => {
        state.supplierListNoticeError = supplierListNoticeError;
      }),
    setSupplierListNoticeStatus: (supplierListNoticeStatus) =>
      set((state) => {
        state.supplierListNoticeStatus = supplierListNoticeStatus;
      }),
    resetState: () => {
      set(initialState);
    },
  })),
  shallow
);
